import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { searchTripsByLead } from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Search, Loader2, FileText } from "lucide-react";

const eur = (n) =>
  (Number(n) || 0).toLocaleString("es-ES", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2,
  });

const TYPE_LABEL = { advance: "Adelanto", final: "Final" };
const STATUS_BADGE = {
  draft: { label: "Borrador", className: "bg-amber-100 text-amber-900" },
  issued: { label: "Emitida", className: "bg-emerald-100 text-emerald-900" },
  sent: { label: "Enviada", className: "bg-sky-100 text-sky-900" },
  paid: { label: "Pagada", className: "bg-violet-100 text-violet-900" },
};

export default function SearchTrips() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Debounce search
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]); setCount(0); return;
    }
    setLoading(true);
    const tid = setTimeout(async () => {
      try {
        const d = await searchTripsByLead(term);
        setResults(d.results || []);
        setCount(d.count || 0);
      } catch {
        setResults([]); setCount(0);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(tid);
  }, [q]);

  // Group by client name so the same person across multiple invoices is visible
  const grouped = results.reduce((acc, r) => {
    const k = r.client_name;
    (acc[k] = acc[k] || []).push(r);
    return acc;
  }, {});

  return (
    <div data-testid="search-trips-page" className="space-y-6 max-w-5xl">
      <header>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">
          Histórico
        </div>
        <h1 className="font-cabinet text-3xl font-bold tracking-tight text-slate-900">
          Buscar cliente
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Encuentra todas las facturas (adelanto y final) en las que aparece un cliente.
        </p>
      </header>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Escribe parte del nombre… (mín. 2 caracteres)"
          className="pl-9 h-11 text-sm"
          data-testid="search-input"
        />
        {loading && (
          <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
        )}
      </div>

      {q.trim().length >= 2 && !loading && (
        <div className="text-xs text-slate-500" data-testid="search-summary">
          {count === 0 ? (
            <span>Sin resultados para <strong className="text-slate-700">"{q}"</strong></span>
          ) : (
            <span>
              {count} {count === 1 ? "línea encontrada" : "líneas encontradas"} ·{" "}
              {Object.keys(grouped).length} {Object.keys(grouped).length === 1 ? "cliente" : "clientes"}
            </span>
          )}
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(grouped).map(([name, rows]) => (
          <div key={name} data-testid={`search-group-${name}`} className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center gap-3">
              <span className="font-cabinet font-semibold text-slate-900">{name}</span>
              <span className="text-xs text-slate-500">
                · {rows.length} {rows.length === 1 ? "factura" : "facturas"}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr className="border-b border-slate-100">
                  <th className="text-left px-4 py-2 font-medium">Factura</th>
                  <th className="text-left px-4 py-2 font-medium">Tipo</th>
                  <th className="text-left px-4 py-2 font-medium">Estado</th>
                  <th className="text-left px-4 py-2 font-medium">Emisión</th>
                  <th className="text-left px-4 py-2 font-medium">Arrival</th>
                  <th className="text-right px-4 py-2 font-medium">Ingresado</th>
                  <th className="text-right px-4 py-2 font-medium">Adelanto</th>
                  <th className="text-right px-4 py-2 font-medium">Final</th>
                  <th className="text-right px-4 py-2 font-medium">Profit</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const sb = STATUS_BADGE[r.invoice_status] || { label: r.invoice_status, className: "bg-slate-100" };
                  return (
                    <tr key={`${r.invoice_id}-${i}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                      <td className="px-4 py-2.5 font-mono text-xs">{r.invoice_number}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-[10px] font-medium">
                          {TYPE_LABEL[r.invoice_type] || r.invoice_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge className={`${sb.className} text-[10px] font-medium border-0`}>
                          {sb.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{r.issue_date}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{r.arrival_date || "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{r.income_eur > 0 ? eur(r.income_eur) : "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{r.adelanto_eur > 0 ? eur(r.adelanto_eur) : "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{r.final_eur > 0 ? eur(r.final_eur) : "—"}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{r.profit_share > 0 ? eur(r.profit_share) : "—"}</td>
                      <td className="px-2 py-2.5">
                        <Link
                          to={`/factura/${r.invoice_id}`}
                          className="text-slate-500 hover:text-slate-900"
                          data-testid={`search-open-${r.invoice_number}`}
                          title="Abrir factura"
                        >
                          <FileText size={14} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
